import React from 'react';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/components/common/BaseDialog';
import type { SessionExpiredReason } from '@/api/axios/axiosClient';

interface SessionExpiredDialogProps {
  open: boolean;
  /** Why the session ended — varies the message copy. */
  reason?: SessionExpiredReason;
  /** Called when the user acknowledges — caller should redirect to Home (Discover). */
  onConfirm: () => void;
}

/**
 * Dialog shown when the user's session ends (idle timeout, inactivity kick, or a
 * 401). Acknowledging redirects the user back to the Discover (Home) page.
 */
export const SessionExpiredDialog: React.FC<SessionExpiredDialogProps> = ({
  open,
  reason = 'unknown',
  onConfirm,
}) => {
  const { t } = useTranslation();

  // Inactivity (client idle kick or backend idle expiry) shares one message;
  // an invalid token is an auth error; everything else is generic.
  const message =
    reason === 'idle' || reason === 'session_expired'
      ? t('session-expired.message-idle', {
          defaultValue:
            'Your session has expired due to inactivity. Please log in again to continue.',
        })
      : reason === 'invalid_token'
        ? t('session-expired.message-auth', {
            defaultValue:
              'Your session is no longer valid. Please log in again to continue.',
          })
        : t('session-expired.message', {
            defaultValue: 'Your session has ended. Please log in again to continue.',
          });

  return (
    <BaseDialog
      open={open}
      title={t('session-expired.title', { defaultValue: 'Session Expired' })}
      maxWidth="xs"
      // Force acknowledgement — no backdrop/escape/close dismissal.
      disableBackdropClose
      actions={
        <Button onClick={onConfirm} variant="contained" autoFocus>
          {t('common.ok', { defaultValue: 'OK' })}
        </Button>
      }
    >
      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
        {message}
      </Typography>
    </BaseDialog>
  );
};

export default SessionExpiredDialog;
